import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AlertTriangle, BadgeCheck, ExternalLink, HelpCircle, Loader2, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, get, errMsg } from "@/lib/api";

export const STATUS = {
  verified: { label: "Verified", cls: "bg-success-tint text-success", Icon: BadgeCheck },
  manual_verified: { label: "Verified manually", cls: "bg-success-tint text-success", Icon: ShieldCheck },
  name_mismatch: { label: "Name mismatch", cls: "bg-risk-tint text-risk", Icon: AlertTriangle },
  not_found: { label: "Not found", cls: "bg-risk-tint text-risk", Icon: XCircle },
  manual_invalid: { label: "Marked not valid", cls: "bg-risk-tint text-risk", Icon: XCircle },
  needs_check: { label: "Needs manual check", cls: "bg-caution-tint text-caution", Icon: HelpCircle },
};
export const effective = (i) => (i.manual ? (i.manual.decision === "verified" ? "manual_verified" : "manual_invalid") : i.status || "needs_check");
const when = (d) => new Date(d).toLocaleString();

function Decide({ item, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const send = async (decision) => { await onDone(item, decision, note); setOpen(false); setNote(""); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button size="sm" variant="outline" data-testid={`cred-decide-${item.key}`}>Decide</Button></PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3">
        <p className="text-sm font-medium">Record a manual decision</p>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" aria-label="Note" data-testid={`cred-note-${item.key}`} />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => send("verified")} data-testid={`cred-mark-verified-${item.key}`}>Mark as verified</Button>
          <Button size="sm" variant="outline" onClick={() => send("not_valid")} data-testid={`cred-mark-invalid-${item.key}`}>Mark as not valid</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ item, staff, onDecide, onUndo }) {
  const s = STATUS[effective(item)];
  return (
    <motion.li layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2 px-5 py-4" data-testid={`cred-item-${item.key}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[200px] flex-1">
          <p className="text-sm font-medium">{item.name}</p>
          <p className="text-xs text-ink-muted">{[item.issuer, item.date, item.credential_id && `ID ${item.credential_id}`].filter(Boolean).join(" · ")}</p>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`cred-status-${item.key}`}><s.Icon size={12} aria-hidden="true" /> {s.label}</span>
        {staff && (item.manual ? <Button size="sm" variant="ghost" onClick={() => onUndo(item)} data-testid={`cred-undo-${item.key}`}>Undo</Button> : <Decide item={item} onDone={onDecide} />)}
      </div>
      <p className="text-xs text-ink-secondary" data-testid={`cred-reason-${item.key}`}>{item.reason}</p>
      {item.manual && <p className="text-xs text-ink-secondary" data-testid={`cred-manual-${item.key}`}>{item.manual.decision === "verified" ? "Verified manually" : "Marked not valid"} by {item.manual.by} · {when(item.manual.at)}{item.manual.note ? ` — “${item.manual.note}”` : ""}</p>}
      <div className="flex flex-wrap gap-3 text-xs">
        {item.source_url && <a href={item.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-brand hover:underline" data-testid={`cred-source-${item.key}`}>{item.source ? `Open on ${item.source}` : "Open link"} <ExternalLink size={11} /></a>}
        {(item.links || []).map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-brand hover:underline">{l.label} <ExternalLink size={11} /></a>)}
      </div>
    </motion.li>
  );
}

export function CredentialsSection({ c, staff }) {
  const qc = useQueryClient();
  const key = ["credentials", c.id];
  const { data } = useQuery({ queryKey: key, queryFn: () => get(`/candidates/${c.id}/credentials`), refetchInterval: (q) => (q.state.data?.status === "checking" ? 2500 : false) });
  const put = (d) => qc.setQueryData(key, d);
  const run = async (p) => { try { put((await p).data); } catch (e) { toast.error(errMsg(e)); } };
  if (!data) return null;
  const certs = data.items.filter((i) => i.kind === "cert");
  const bar = data.items.filter((i) => i.kind === "bar");
  const sm = data.summary;
  return (
    <section className="panel overflow-hidden" data-tour="pt-credentials" data-testid="credentials-section">
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
        <h2 className="text-base font-semibold">Credentials</h2>
        {sm.total > 0 && <CredBadge s={sm} />}
        {data.status === "checking" && <span className="flex items-center gap-1 text-xs text-ink-muted" role="status" data-testid="cred-checking"><Loader2 size={12} className="animate-spin" /> Checking public sources…</span>}
        {staff && <Button size="sm" variant="outline" className="ml-auto" disabled={data.status === "checking"} onClick={() => run(api.post(`/candidates/${c.id}/credentials/recheck`))} data-testid="cred-recheck-btn"><RefreshCw size={14} /> Re-check</Button>}
      </div>
      {data.bar_missing_for?.length > 0 && <p role="alert" className="flex items-center gap-2 border-b border-line bg-caution-tint px-5 py-2 text-sm text-caution" data-testid="cred-bar-missing"><AlertTriangle size={14} aria-hidden="true" /> Bar enrolment not found in CV — required for {data.bar_missing_for.map((j) => j.title).join(", ")}</p>}
      <ul className="divide-y divide-line">
        {bar.map((i) => <Row key={i.key} item={i} staff={staff} onDecide={(it, d, n) => run(api.post(`/candidates/${c.id}/credentials/${it.key}/decision`, { decision: d, note: n }))} onUndo={(it) => run(api.delete(`/candidates/${c.id}/credentials/${it.key}/decision`))} />)}
        {certs.map((i) => <Row key={i.key} item={i} staff={staff} onDecide={(it, d, n) => run(api.post(`/candidates/${c.id}/credentials/${it.key}/decision`, { decision: d, note: n }))} onUndo={(it) => run(api.delete(`/candidates/${c.id}/credentials/${it.key}/decision`))} />)}
      </ul>
      {!certs.length && data.status !== "checking" && <p className="px-5 py-4 text-sm text-ink-muted" data-testid="cred-none">No certifications found in this CV</p>}
      <p className="border-t border-line px-5 py-2 text-xs text-ink-muted">"Verified" means the public record was found and the holder name matches this candidate. Checks use free public sources only.</p>
    </section>
  );
}

export function CredBadge({ s, testId = "cred-badge" }) {
  if (!s || !s.total) return null;
  const parts = [s.verified && `${s.verified} verified`, s.needs_check && `${s.needs_check} need${s.needs_check === 1 ? "s" : ""} check`, s.issues && `${s.issues} issue${s.issues === 1 ? "" : "s"}`].filter(Boolean);
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${s.issues ? "bg-risk-tint text-risk" : s.needs_check ? "bg-caution-tint text-caution" : "bg-success-tint text-success"}`} data-testid={testId}><ShieldCheck size={11} aria-hidden="true" /> {parts.join(" · ")}</span>;
}

export function BarMissingChip({ testId }) {
  return <span className="inline-flex items-center gap-1 rounded-full bg-caution-tint px-2 py-0.5 text-[11px] font-medium text-caution" data-testid={testId}><AlertTriangle size={11} aria-hidden="true" /> Bar enrolment not found in CV</span>;
}
